import { useEffect, useState } from 'react'
import { useSearchParams, useNavigate, Link } from 'react-router-dom'
import { useMutation } from '@tanstack/react-query'
import { Loader2, CheckCircle, XCircle } from 'lucide-react'
import { apiClient, apiErrorDetail } from '@/lib/api-client'

// #407 — the API emails `${FRONTEND_URL}/verify-email?token=...`
// (services/management/src/routes/email-verification.ts), a query param
// rather than a path segment, so this page must read `token` from the query
// string, not a route param.
export function VerifyEmailPage() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading')
  const [errorDetail, setErrorDetail] = useState<string | null>(null)

  const token = searchParams.get('token')

  const confirmVerification = useMutation({
    mutationFn: async () => {
      if (!token) throw new Error('missing token')
      return apiClient.verifyEmail(token)
    },
    onSuccess: () => {
      setStatus('success')
      setTimeout(() => navigate('/dashboard'), 2000)
    },
    onError: (error: unknown) => {
      setStatus('error')
      setErrorDetail(apiErrorDetail(error, 'The verification link is invalid or has expired.'))
    },
  })

  useEffect(() => {
    if (!token) {
      setStatus('error')
      setErrorDetail('This verification link is missing its token.')
      return
    }
    confirmVerification.mutate()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token])

  if (status === 'loading') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4">
        <div className="w-full max-w-md space-y-4 rounded-lg bg-white p-8 text-center shadow">
          <Loader2 className="mx-auto h-12 w-12 animate-spin text-primary-600" />
          <h2 className="text-2xl font-bold">Verifying Email</h2>
          <p className="text-gray-600">Please wait while we verify your email address...</p>
        </div>
      </div>
    )
  }

  if (status === 'success') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4">
        <div className="w-full max-w-md space-y-4 rounded-lg bg-white p-8 text-center shadow">
          <CheckCircle className="mx-auto h-12 w-12 text-green-500" />
          <h2 className="text-2xl font-bold">Email Verified</h2>
          <p className="text-gray-600">Your email address has been verified. Redirecting...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4">
      <div className="w-full max-w-md space-y-4 rounded-lg bg-white p-8 text-center shadow">
        <XCircle className="mx-auto h-12 w-12 text-red-500" />
        <h2 className="text-2xl font-bold">Verification Failed</h2>
        <p className="text-gray-600">{errorDetail}</p>
        <Link to="/auth/login" className="inline-block font-medium text-primary-600 hover:text-primary-500">
          Go to Login
        </Link>
      </div>
    </div>
  )
}
